interface HeroSectionProps {
  onPrimaryCTA: () => void;
}

export default function HeroSection({ onPrimaryCTA }: HeroSectionProps) {
  return (
    <>
      <section className="bg-white pt-36 pb-12 lg:pb-24 px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div className="flex flex-col gap-4 md:gap-6">
            <div className="inline-flex items-center gap-2 bg-alt-bg border border-gray-200 rounded-full px-4 py-1.5 self-start">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs font-semibold text-navy">Active in all 50 States — now enrolling</span>
            </div>

            {/* Mobile H1 */}
            <h1
              className="block md:hidden text-navy font-extrabold leading-tight"
              style={{ fontSize: "clamp(36px, 5vw, 60px)", lineHeight: "1.1" }}
            >
              Be First. Win the Closing.
            </h1>
            {/* Desktop H1 */}
            <h1
              className="hidden md:block text-navy font-extrabold leading-tight"
              style={{ fontSize: "clamp(40px, 5vw, 60px)", lineHeight: "1.1" }}
            >
              Own the Moment That Wins Your Next Closing
            </h1>

            {/* Mobile: subheading + 8–10 closings grouped tightly */}
            <div className="flex flex-col gap-3 md:hidden">
              <p className="text-[#4a5568] text-base leading-relaxed" style={{ lineHeight: "1.65" }}>
                It reaches agents the moment deals are forming —{" "}
                <strong className="text-navy font-semibold">before they choose a title company.</strong>
              </p>
              <div className="flex items-center gap-2">
                <span className="w-1 h-4 bg-primary rounded-full flex-shrink-0" />
                <p className="text-navy font-bold text-sm">
                  8–10 closings covers the cost.
                </p>
              </div>
            </div>

            {/* Desktop subheading */}
            <p className="hidden md:block text-[#4a5568] text-lg leading-relaxed" style={{ lineHeight: "1.65" }}>
              AI-powered outreach to agents and loan officers — triggered by{" "}
              <strong className="text-navy font-semibold">real-time transaction activity.</strong>
            </p>

            {/* Desktop second line */}
            <p className="hidden md:block text-[#4a5568] text-base" style={{ lineHeight: "1.6" }}>
              Be first in the conversation. Be top of mind before the closing is decided.
            </p>

            {/* Mobile CTA block */}
            <div className="flex flex-col gap-2 md:hidden pt-1">
              <button
                onClick={onPrimaryCTA}
                className="inline-flex items-center justify-center gap-2 bg-primary text-white text-base font-semibold px-7 py-3.5 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap w-full"
              >
                Check If Your Market Is Still Available
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </button>
              <a
                href="#demo"
                className="inline-flex items-center justify-center gap-2 border-2 text-base font-semibold px-7 py-3.5 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap w-full"
                style={{ borderColor: "#19a2fd", color: "#19a2fd" }}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line text-lg" />
                </span>
                Hear a Real Demo Call
              </a>
              <p className="text-[#4a5568] text-xs font-semibold text-center pt-1">
                Only one title partner per market.
              </p>
            </div>

            {/* Desktop CTA block */}
            <div className="hidden md:flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={onPrimaryCTA}
                className="inline-flex items-center justify-center gap-2 bg-primary text-white text-base font-semibold px-7 py-3.5 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Check Market Availability
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </button>
              <a
                href="#demo"
                className="inline-flex items-center justify-center gap-2 border-2 text-secondary text-base font-semibold px-7 py-3.5 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                style={{ borderColor: "#19a2fd", color: "#19a2fd" }}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line text-lg" />
                </span>
                Hear a Real Demo Call
              </a>
            </div>

            {/* Trust signals */}
            <div className="flex flex-nowrap sm:flex-wrap items-center justify-between sm:justify-start gap-2 sm:gap-6 pt-4 border-t border-gray-100">
              {[
                { icon: "ri-shield-check-line", text: "SOC 2 Type II" },
                { icon: "ri-scales-3-line", text: "TCPA Compliant" },
                { icon: "ri-lock-line", text: "Secure by Default" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-1 sm:gap-2">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-primary flex-shrink-0">
                    <i className={`${item.icon} text-xs sm:text-sm`} />
                  </span>
                  <span className="text-[10px] sm:text-xs font-semibold text-[#4a5568] whitespace-nowrap">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: UI Visual */}
          <div className="hidden lg:relative lg:flex items-center justify-center">
            <div
              className="absolute inset-0 rounded-3xl opacity-40"
              style={{ background: "radial-gradient(ellipse at 60% 40%, #19a2fd22 0%, transparent 70%)" }}
            />

            {/* Phone mockup */}
            <div className="relative z-10 flex flex-col items-center">
              <div
                className="bg-navy rounded-[2.5rem] p-4 shadow-2xl w-64"
                style={{ boxShadow: "0 40px 80px rgba(11,31,51,0.18), 0 10px 30px rgba(241,102,58,0.08)" }}
              >
                {/* Phone top */}
                <div className="bg-[#162d47] rounded-[2rem] overflow-hidden">
                  {/* Status bar */}
                  <div className="flex items-center justify-between px-4 pt-3 pb-1">
                    <span className="text-white/40 text-[10px] font-medium">9:41</span>
                    <div className="flex gap-1">
                      <i className="ri-wifi-line text-white/40 text-xs" />
                      <i className="ri-battery-2-charge-line text-white/40 text-xs" />
                    </div>
                  </div>

                  {/* Call UI */}
                  <div className="px-4 pb-6 pt-2 flex flex-col items-center gap-3">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-secondary to-blue-300 flex items-center justify-center text-white font-bold text-xl">
                      SA
                    </div>
                    <div className="text-center">
                      <p className="text-white font-semibold text-sm">Sarah Atkinson</p>
                      <p className="text-white/50 text-xs">Licensed Agent · Austin, TX</p>
                    </div>

                    <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-full px-3 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-green-300 text-xs font-semibold">AI Call in Progress</span>
                    </div>

                    <div className="text-white/30 text-xs font-mono">0:42</div>

                    <div className="flex gap-4 mt-1">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 cursor-pointer">
                        <i className="ri-mic-line text-white/60 text-sm" />
                      </div>
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500 cursor-pointer">
                        <i className="ri-phone-line text-white text-base rotate-[135deg]" />
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 cursor-pointer">
                        <i className="ri-volume-up-line text-white/60 text-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <div
                className="absolute -left-16 top-8 bg-white rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
                style={{ boxShadow: "0 8px 32px rgba(11,31,51,0.12)" }}
              >
                <div className="w-7 h-7 flex items-center justify-center bg-orange-50 rounded-lg">
                  <i className="ri-home-4-line text-primary text-sm" />
                </div>
                <div>
                  <p className="text-navy text-[11px] font-bold leading-tight">New Listing Detected</p>
                  <p className="text-[#4a5568] text-[10px]">1847 Ridgewood Ave</p>
                </div>
              </div>

              <div
                className="absolute -right-12 top-24 bg-white rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
                style={{ boxShadow: "0 8px 32px rgba(11,31,51,0.12)" }}
              >
                <div className="w-7 h-7 flex items-center justify-center bg-blue-50 rounded-lg">
                  <i className="ri-time-line text-secondary text-sm" />
                </div>
                <div>
                  <p className="text-navy text-[11px] font-bold leading-tight">Pending Closing</p>
                  <p className="text-[#4a5568] text-[10px]">$485,000 · 3 days ago</p>
                </div>
              </div>

              <div
                className="absolute -right-10 bottom-12 bg-white rounded-xl px-3.5 py-2.5 flex items-center gap-2.5"
                style={{ boxShadow: "0 8px 32px rgba(11,31,51,0.12)" }}
              >
                <div className="w-7 h-7 flex items-center justify-center bg-green-50 rounded-lg">
                  <i className="ri-check-line text-green-500 text-sm" />
                </div>
                <div>
                  <p className="text-navy text-[11px] font-bold leading-tight">Call Completed</p>
                  <p className="text-[#4a5568] text-[10px]">Routed to closer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scarcity banner */}
      <div className="bg-[#0B2A3A] py-3 sm:py-4 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 sm:gap-3">
          <span className="w-5 h-5 items-center justify-center flex-shrink-0 hidden sm:flex">
            <i className="ri-map-pin-2-fill text-[#F1663A] text-base" />
          </span>
          <p className="text-white font-medium text-center text-xs sm:text-sm">
            Only <span className="text-[#F1663A] font-bold">ONE</span> title partner per market.{" "}
            <span className="text-white/80 hidden sm:inline">Once your market is claimed, it&apos;s closed.</span>
          </p>
          <button
            onClick={onPrimaryCTA}
            className="bg-[#F1663A] text-white font-bold rounded-lg hover:bg-[#d9552a] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 text-xs px-3 py-2 sm:px-4 sm:py-2 sm:ml-4"
          >
            Check Availability
          </button>
        </div>
      </div>
    </>
  );
}
